'use client'

import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
} from 'firebase/auth'
import { ArrowLeft, Check, Eye, EyeOff, Lock, Mail, ShieldCheck } from 'lucide-react'
import { isNativeApp, PLAY_STORE_URL } from '@/lib/native-app'
import { firebaseAuth } from '@/lib/firebase'
import { getDeviceHash } from '@/lib/device-fingerprint'
import { isValidEmailSyntax } from '@/lib/email-policy'
import { PASSWORD_MAX, PASSWORD_RULE_LABELS, checkPassword } from '@/lib/auth-policy'

// Login / Sign up / Forgot password. Talks to Firebase Auth directly (Google button is passed
// in from app/page.tsx, which owns the Google + Android native flow) and to /api/auth/guard
// for the server-side D1 rate limits. When a sign-in succeeds, onAuthStateChanged in
// app/page.tsx takes over - this component simply unmounts.

type Mode = 'login' | 'signup' | 'forgot'

type Props = {
  onGoogle: () => void
  googleBusy: boolean
  googleError?: string
  initialMode?: 'login' | 'signup'
}

const WRONG_CREDENTIAL = new Set(['auth/wrong-password', 'auth/user-not-found', 'auth/invalid-credential', 'auth/invalid-login-credentials'])

function friendlyError(code: string): string {
  switch (code) {
    case 'auth/invalid-email': return 'That email address looks invalid.'
    case 'auth/user-disabled': return 'This account has been disabled. Contact support if you think this is a mistake.'
    case 'auth/too-many-requests': return 'Too many attempts. Please wait a few minutes, or reset your password.'
    case 'auth/network-request-failed': return 'Network problem. Check your connection and try again.'
    case 'auth/timeout': return 'The connection is slow. Please try again.'
    case 'auth/email-already-in-use': return "We couldn't create an account with this email. Log in instead, or reset your password."
    case 'auth/weak-password': return 'Password is too weak. Follow the checklist below the password field.'
    case 'auth/password-does-not-meet-requirements': return 'Password does not meet the security requirements. Follow the checklist below the password field.'
    case 'auth/operation-not-allowed': return 'Email sign-up is temporarily unavailable. Please use "Continue with Google" or try again later.'
    case 'auth/unauthorized-domain': return 'Sign-in is not available on this address. Please open the official Animora app or website.'
    case 'auth/firebase-app-check-token-is-invalid':
    case 'auth/app-check-token-invalid': return 'Security check failed. Close and reopen the app, then try again.'
    case 'auth/internal-error': return 'Sign-in service had a hiccup (auth/internal-error). Please try again in a moment.'
    case 'auth/invalid-api-key':
    case 'auth/api-key-not-valid.-please-pass-a-valid-api-key.': return 'Sign-in is temporarily unavailable (auth/invalid-api-key). Please try again later.'
    case 'auth/requires-recent-login': return 'Please log in again.'
    case 'auth/web-storage-unsupported':
    case 'auth/operation-not-supported-in-this-environment': return 'This browser is blocking storage needed to sign in. Turn off private mode or open the site in Chrome, then try again.'
    default: return `Something went wrong${code ? ` (${code})` : ''}. Please try again.`
  }
}

// Typos in the most common mail providers. Such an address can never receive the verification email, and every
// try would also eat into the sign-up limits - so we catch it before anything is sent.
const DOMAIN_TYPOS: Record<string, string> = {
  'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmil.com': 'gmail.com', 'gamil.com': 'gmail.com', 'gmail.con': 'gmail.com', 'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com', 'gmaill.com': 'gmail.com', 'gnail.com': 'gmail.com', 'gmeil.com': 'gmail.com', 'gimail.com': 'gmail.com',
  'yaho.com': 'yahoo.com', 'yahooo.com': 'yahoo.com', 'yahoo.con': 'yahoo.com', 'hotmial.com': 'hotmail.com', 'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com', 'outlook.con': 'outlook.com', 'iclod.com': 'icloud.com', 'icloud.con': 'icloud.com',
}

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z" />
    <path fill="#FBBC05" d="M10.5 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.9-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
)

const GOOGLE_FAIL_KEY = 'animora_google_fails'
const GOOGLE_FAIL_LIMIT = 2
const GOOGLE_FAIL_WINDOW_MS = 60 * 60 * 1000
const PLAY_SERVICES_URL = 'https://play.google.com/store/apps/details?id=com.google.android.gms'

function readGoogleFails(): number {
  try {
    const raw = JSON.parse(localStorage.getItem(GOOGLE_FAIL_KEY) || 'null')
    if (raw && typeof raw.n === 'number' && Date.now() - Number(raw.t || 0) < GOOGLE_FAIL_WINDOW_MS) return raw.n
  } catch { /* private mode / bad json */ }
  return 0
}
function bumpGoogleFails(): number {
  const n = readGoogleFails() + 1
  try { localStorage.setItem(GOOGLE_FAIL_KEY, JSON.stringify({ n, t: Date.now() })) } catch { /* ignore */ }
  return n
}

export function AuthScreen({ onGoogle, googleBusy, googleError, initialMode = 'login' }: Props) {
  const [mode, setMode] = useState<Mode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [agree, setAgree] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [hp, setHp] = useState('') // honeypot: real people never see or fill this
  const [caps, setCaps] = useState(false)
  const [lockUntil, setLockUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [resetCooldownUntil, setResetCooldownUntil] = useState(0)
  const mountedAt = useRef(Date.now())
  const emailRef = useRef<HTMLInputElement>(null)
  const errRef = useRef<HTMLParagraphElement>(null)
  const shownErrorRef = useRef('')
  const [hideGoogleErr, setHideGoogleErr] = useState(false)
  // "Get the Android app" button: website only. Decided after mount so the server HTML and the app (WebView) never disagree.
  const [showGetApp, setShowGetApp] = useState(false)
  useEffect(() => { setShowGetApp(!isNativeApp()) }, [])
  useEffect(() => { setHideGoogleErr(false) }, [googleError])

  // Repeated Google sign-in failures (usually an old Google Play services on Android): after
  // GOOGLE_FAIL_LIMIT failures within an hour, show a warning that points to manual signup / a Play services update.
  const [googleFails, setGoogleFails] = useState(0)
  useEffect(() => { setGoogleFails(readGoogleFails()) }, [])
  useEffect(() => {
    if (!googleError || /cancel|closed/i.test(googleError)) return
    setGoogleFails(bumpGoogleFails())
  }, [googleError])
  const showGoogleHelp = googleFails >= GOOGLE_FAIL_LIMIT

  const cleanEmail = email.trim().toLowerCase()
  const pw = useMemo(() => checkPassword(password, cleanEmail), [password, cleanEmail])
  const mismatch = confirm.length > 0 && confirm !== password

  // 1s ticker only while a lockout / cooldown is running.
  useEffect(() => {
    if (Math.max(lockUntil, resetCooldownUntil) <= Date.now()) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [lockUntil, resetCooldownUntil])

  const lockLeft = Math.max(0, Math.ceil((lockUntil - now) / 1000))
  const resetLeft = Math.max(0, Math.ceil((resetCooldownUntil - now) / 1000))
  const fmt = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s}s`)

  const switchMode = (next: Mode) => {
    if (busy) return
    setMode(next)
    setHideGoogleErr(true)
    setError('')
    setInfo('')
    setPassword('')
    setConfirm('')
    setShowPw(false)
    mountedAt.current = Date.now()
    setTimeout(() => emailRef.current?.focus(), 30)
  }

  const guard = useCallback(async (action: string, extra: Record<string, unknown> = {}, headers: Record<string, string> = {}) => {
    try {
      const res = await fetch('/api/auth/guard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ action, email: cleanEmail, hp, elapsed: Date.now() - mountedAt.current, ...extra }),
      })
      const d = await res.json().catch(() => ({}))
      return { status: res.status, d }
    } catch {
      return { status: 0, d: {} as any }
    }
  }, [cleanEmail, hp])

  // Returns true if the guard says stop (and has already put the reason on screen).
  const blockedBy = (r: { status: number; d: any }): boolean => {
    if (r.status === 429) {
      setLockUntil(Date.now() + (Number(r.d?.retryAfterSec) || 60) * 1000)
      setNow(Date.now())
      setError(r.d?.message || 'Too many attempts. Please try again later.')
      return true
    }
    // Guard itself crashed (5xx, e.g. D1/Worker hiccup) -> fail OPEN. Firebase's own limits still
    // apply, and a broken guard must never stop every login / sign-up. The only 5xx we honour is
    // the explicit "unavailable" reply, which the guard sends on purpose for sign-up / reset.
    if (r.status >= 500 && r.d?.error !== 'unavailable') return false
    if (r.status >= 400) {
      setError(r.d?.message || 'Something went wrong. Please try again.')
      return true
    }
    return false // includes status 0 (offline guard): Firebase's own limits still apply
  }

  const submitLogin = async (e: FormEvent) => {
    e.preventDefault()
    if (busy || lockLeft > 0) return
    setError(''); setInfo('')
    if (!isValidEmailSyntax(cleanEmail)) { setError('Enter a valid email address.'); emailRef.current?.focus(); return }
    if (!password) { setError('Enter your password.'); return }
    setBusy(true)
    try {
      if (blockedBy(await guard('login'))) return
      try { await setPersistence(firebaseAuth, browserLocalPersistence) } catch { /* best effort */ }
      try {
        const cred = await signInWithEmailAndPassword(firebaseAuth, cleanEmail, password)
        // Clears this email+IP failure counter (server verifies the token).
        void guard('login-ok', {}, { Authorization: `Bearer ${await cred.user.getIdToken()}` })
      } catch (err: any) {
        const code: string = err?.code || ''
        if (WRONG_CREDENTIAL.has(code)) {
          const r = await guard('login-fail')
          const left = Number(r.d?.attemptsLeft)
          if (r.status === 200 && Number.isFinite(left) && left <= 0) {
            setLockUntil(Date.now() + 15 * 60_000); setNow(Date.now())
            setError('Too many failed attempts. Login is paused for 15 minutes. You can reset your password instead.')
          } else if (r.status === 200 && Number.isFinite(left) && left <= 2) {
            setError(`Incorrect email or password. ${left} ${left === 1 ? 'attempt' : 'attempts'} left before a temporary lock.`)
          } else {
            setError('Incorrect email or password.')
          }
        } else {
          setError(friendlyError(code))
        }
        if (WRONG_CREDENTIAL.has(code)) setPassword('')
      }
    } finally {
      setBusy(false)
    }
  }

  const submitSignup = async (e: FormEvent) => {
    e.preventDefault()
    if (busy || lockLeft > 0) return
    setError(''); setInfo('')
    if (!isValidEmailSyntax(cleanEmail)) { setError('Enter a valid email address.'); emailRef.current?.focus(); return }
    const fixed = DOMAIN_TYPOS[cleanEmail.split('@')[1] || '']
    if (fixed) { setError(`Did you mean ${cleanEmail.split('@')[0]}@${fixed}? Fix the email, then try again.`); emailRef.current?.focus(); return }
    if (!pw.ok) { setError('Your password does not meet all the requirements yet.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    if (!agree) { setError('Please accept the Terms and Privacy Policy to continue.'); return }
    setBusy(true)
    try {
      let deviceHash = ''
      try { deviceHash = (await getDeviceHash()) || '' } catch { /* optional */ }
      if (blockedBy(await guard('signup', { deviceHash }))) return
      try { await setPersistence(firebaseAuth, browserLocalPersistence) } catch { /* best effort */ }
      try {
        const cred = await createUserWithEmailAndPassword(firebaseAuth, cleanEmail, password)
        // The next screen (verify your email) says a link was sent, so send it right now.
        try { await sendEmailVerification(cred.user) } catch { /* the next screen has a Resend button */ }
      } catch (err: any) {
        const code: string = err?.code || ''
        setError(friendlyError(code))
        // Only wipe the password when it is the problem; a network hiccup should not make people type everything again.
        if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') { setPassword(''); setConfirm('') }
      }
    } finally {
      setBusy(false)
    }
  }

  const submitForgot = async (e: FormEvent) => {
    e.preventDefault()
    if (busy || resetLeft > 0) return
    setError(''); setInfo('')
    if (!isValidEmailSyntax(cleanEmail)) { setError('Enter the email you signed up with.'); return }
    setBusy(true)
    try {
      if (blockedBy(await guard('reset'))) return
      try {
        await sendPasswordResetEmail(firebaseAuth, cleanEmail)
      } catch (err: any) {
        const code: string = err?.code || ''
        // "No such user" is deliberately treated as success: the reply must never reveal
        // which emails have an account.
        if (code !== 'auth/user-not-found' && code !== 'auth/invalid-email') {
          setError(friendlyError(code))
          return
        }
      }
      setInfo('If an account exists for this email, a reset link is on its way. Check your inbox and spam folder.')
      setResetCooldownUntil(Date.now() + 60_000); setNow(Date.now())
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (shownErrorRef.current) errRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [error, googleError])
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => setCaps(e.getModifierState?.('CapsLock') ?? false)

  const locked = lockLeft > 0
  const shownError = error || (hideGoogleErr ? '' : googleError) || ''
  shownErrorRef.current = shownError

  return (
    <main className="app-shell au-shell">
      <div className="au-card">
        <div className="au-brand">
          <img src="/brand-logo.webp" alt="" width={40} height={40} />
          <strong>Ani<span>mora</span> <em className="brand-social-tag">Social</em></strong>
        </div>

        {mode !== 'forgot' ? (
          <>
            <h1 className="au-title">{mode === 'login' ? 'Welcome back' : 'Create your account'}</h1>
            <p className="au-sub">{mode === 'login' ? 'Log in to pick up your chats and feed.' : 'Free, and protected with email verification.'}</p>

            <div className="au-seg" role="tablist" aria-label="Log in or sign up" data-pos={mode}>
              <span className="au-seg-thumb" aria-hidden="true" />
              <button type="button" role="tab" aria-selected={mode === 'login'} onClick={() => switchMode('login')}>Log in</button>
              <button type="button" role="tab" aria-selected={mode === 'signup'} onClick={() => switchMode('signup')}>Sign up</button>
            </div>
          </>
        ) : (
          <>
            <button type="button" className="au-back" onClick={() => switchMode('login')}><ArrowLeft size={16} /> Back to log in</button>
            <h1 className="au-title">Reset your password</h1>
            <p className="au-sub">Enter your email and we&apos;ll send you a link to choose a new password.</p>
          </>
        )}

        <form
          onSubmit={mode === 'login' ? submitLogin : mode === 'signup' ? submitSignup : submitForgot}
          noValidate
          autoComplete="on"
          className="au-form"
        >
          {/* Honeypot - hidden from people and screen readers, tempting to bots. */}
          <div className="au-hp" aria-hidden="true">
            <label>Leave this field empty<input type="text" name="ax_trap_field" tabIndex={-1} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-form-type="other" value={hp} onChange={(e) => setHp(e.target.value)} /></label>
          </div>

          <label className="au-field">
            <span>Email</span>
            <div className="au-input">
              <Mail size={16} aria-hidden="true" />
              <input
                ref={emailRef}
                type="email"
                name="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="you@example.com"
                maxLength={254}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </label>

          {mode !== 'forgot' && (
            <label className="au-field">
              <span>Password</span>
              <div className="au-input">
                <Lock size={16} aria-hidden="true" />
                <input
                  type={showPw ? 'text' : 'password'}
                  name="password"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder={mode === 'login' ? 'Your password' : 'Create a strong password'}
                  maxLength={PASSWORD_MAX}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={onKey}
                  onKeyDown={onKey}
                  required
                />
                <button type="button" className="au-eye" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} aria-pressed={showPw}>
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {caps && <small className="au-warn">Caps Lock is on.</small>}
            </label>
          )}

          {mode === 'login' && (
            <button type="button" className="au-link au-forgot" onClick={() => switchMode('forgot')}>Forgot password?</button>
          )}

          {mode === 'signup' && (
            <>
              <div className="au-meter" aria-hidden="true" data-score={pw.score}>
                <i /><i /><i /><i />
                <em>{password ? ['Too weak', 'Weak', 'Fair', 'Good', 'Strong'][pw.score] : 'Password strength'}</em>
              </div>
              <ul className="au-rules" aria-label="Password requirements">
                {PASSWORD_RULE_LABELS.map(({ key, label }) => (
                  <li key={key} data-ok={pw.rules[key] && password.length > 0 ? 'true' : 'false'}>
                    <span aria-hidden="true">{pw.rules[key] && password.length > 0 ? <Check size={14} strokeWidth={3} /> : null}</span>{label}
                  </li>
                ))}
              </ul>

              <label className="au-field">
                <span>Confirm password</span>
                <div className="au-input" data-invalid={mismatch ? 'true' : undefined}>
                  <ShieldCheck size={16} aria-hidden="true" />
                  <input
                    type={showPw ? 'text' : 'password'}
                    name="confirm-password"
                    autoComplete="new-password"
                    placeholder="Type it again"
                    maxLength={PASSWORD_MAX}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                  />
                  {confirm.length > 0 && !mismatch && <Check size={18} className="au-match" aria-label="Passwords match" />}
                </div>
                {mismatch && <small className="au-warn">Passwords don&apos;t match yet.</small>}
              </label>

              <label className="au-agree">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span>I agree to the <a href="/terms" target="_blank" rel="noopener">Terms</a> and <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>, and I&apos;m 13 or older.</span>
              </label>
            </>
          )}

          {shownError && <p ref={errRef} role="alert" className="au-msg au-msg-error">{shownError}</p>}
          {info && <p role="status" className="au-msg au-msg-ok">{info}</p>}
          {locked && <p className="au-msg au-msg-lock">Try again in {fmt(lockLeft)}</p>}

          <button
            type="submit"
            className="au-btn"
            data-busy={busy ? 'true' : undefined}
            disabled={busy || locked || (mode === 'forgot' && resetLeft > 0)}
          >
            <span className="au-btn-label">
              {busy
                ? (mode === 'login' ? 'Logging in' : mode === 'signup' ? 'Creating account' : 'Sending link')
                : mode === 'login' ? 'Log in' : mode === 'signup' ? 'Create account' : resetLeft > 0 ? `Resend in ${fmt(resetLeft)}` : 'Send reset link'}
            </span>
            {busy && <span className="au-spin" aria-hidden="true" />}
          </button>
        </form>

        {mode !== 'forgot' && (
          <>
            <div className="au-or" aria-hidden="true"><span>or</span></div>
            <button type="button" className="au-btn au-btn-google" onClick={onGoogle} disabled={googleBusy || busy}>
              <GoogleG />
              <span className="au-btn-label">{googleBusy ? 'Opening Google' : 'Continue with Google'}</span>
              {googleBusy && <span className="au-spin au-spin-dark" aria-hidden="true" />}
            </button>
            {showGoogleHelp && (
              <div role="alert" className="au-msg au-msg-error" style={{ display: 'grid', gap: 8, textAlign: 'left', marginTop: 12 }}>
                <strong>Google sign-in keeps failing on this device.</strong>
                <span>Please create a manual account with your email and password, or update Google Play services from the Play Store and try again.</span>
                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {mode !== 'signup' && (
                    <button type="button" className="au-btn" style={{ flex: '1 1 150px' }} onClick={() => { switchMode('signup'); window.scrollTo?.({ top: 0, behavior: 'smooth' }) }}>
                      <span className="au-btn-label">Create manual account</span>
                    </button>
                  )}
                  <a className="au-btn au-btn-google" style={{ flex: '1 1 150px', textDecoration: 'none', justifyContent: 'center' }} href={PLAY_SERVICES_URL} target="_blank" rel="noopener noreferrer">
                    <span className="au-btn-label">Update Google services</span>
                  </a>
                </span>
              </div>
            )}
          </>
        )}

        {showGetApp && (
          <a className="au-getapp" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <span aria-hidden="true">▶</span> Get the Animora app on Google Play
          </a>
        )}

        <p className="au-foot">
          <a href="/terms">Terms</a><a href="/privacy">Privacy</a><a href="/cookies">Cookies</a><a href="/about">About</a><a href="/contact">Support</a>
        </p>
      </div>
    </main>
  )
}
